import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { SellerVerifyResponse } from '@m8-app/contracts';
import { logoutSeller } from '../api/seller.js';

type SellerInfo = SellerVerifyResponse['seller'];

interface AuthState {
  accessToken: string | null;
  seller: SellerInfo | null;
}

interface AuthContextValue extends AuthState {
  login: (accessToken: string, refreshToken: string, seller: SellerInfo) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function loadInitialState(): AuthState {
  const accessToken = localStorage.getItem('seller_accessToken');
  const raw = localStorage.getItem('seller_user');
  try {
    const seller = raw ? (JSON.parse(raw) as SellerInfo) : null;
    return { accessToken, seller };
  } catch {
    return { accessToken: null, seller: null };
  }
}

export function SellerAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(loadInitialState);

  const login = useCallback(
    (accessToken: string, refreshToken: string, seller: SellerInfo) => {
      localStorage.setItem('seller_accessToken', accessToken);
      localStorage.setItem('seller_refreshToken', refreshToken);
      localStorage.setItem('seller_user', JSON.stringify(seller));
      setState({ accessToken, seller });
    },
    [],
  );

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem('seller_refreshToken');
    if (refreshToken) {
      try {
        await logoutSeller(refreshToken);
      } catch {
        // ignore — clear local state regardless
      }
    }
    localStorage.removeItem('seller_accessToken');
    localStorage.removeItem('seller_refreshToken');
    localStorage.removeItem('seller_user');
    setState({ accessToken: null, seller: null });
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useSellerAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useSellerAuth must be used inside SellerAuthProvider');
  return ctx;
}
